package com.patronat.top.features.usermanagement.presentation.create

import com.patronat.top.core.base.BaseViewModel
import com.patronat.top.core.presentation.AlertController
import com.patronat.top.core.presentation.NotificationType
import com.patronat.top.core.utils.UiText
import com.patronat.top.dto.ImageType
import com.patronat.top.features.usermanagement.domain.usecase.CreateUserUseCase
import com.patronat.top.features.usermanagement.domain.usecase.GetUserByIdCommand
import com.patronat.top.features.usermanagement.domain.usecase.GetUserByIdUseCase
import com.patronat.top.features.usermanagement.domain.usecase.UpdateUserCommand
import com.patronat.top.features.usermanagement.domain.usecase.UpdateUserUseCase
import com.patronat.top.features.usermanagement.domain.usecase.command.CreateUserCommand
import com.patronat.top.features.usermanagement.presentation.mappers.toDomainModel
import com.patronat.top.library.media.data.FileSource
import com.patronat.top.library.media.domain.command.UploadImageCommand
import com.patronat.top.library.media.domain.usecase.UploadImageUseCase
import top.design_system.generated.resources.Res
import top.design_system.generated.resources.error_alert_title

class CreateUserViewModel(
    private val alertController: AlertController,
    private val uploadImageUseCase: UploadImageUseCase,
    private val createUserUseCase: CreateUserUseCase,
    private val getUserByIdUseCase: GetUserByIdUseCase,
    private val updateUserUseCase: UpdateUserUseCase
) : BaseViewModel<CreateOrUpdateUserState, CreateOrUpdateUserEvent, CreateUpdateUserEffect>(
    initialState = CreateOrUpdateUserState()
) {

    override fun onEvent(event: CreateOrUpdateUserEvent) {
        when (event) {
            is CreateOrUpdateUserEvent.Initialize -> {
                updateState { it.copy(role = event.role, userId = event.userId) }
                event.userId?.let { loadUserData(it) }
            }
            is CreateOrUpdateUserEvent.OnDataChanged -> {
                updateState {
                    it.copy(
                        firstName = event.firstName,
                        lastName = event.lastName,
                        email = event.email,
                        phone = event.phone
                    )
                }
            }
            is CreateOrUpdateUserEvent.OnStatusChanged -> {
                updateState { it.copy(status = event.status) }
            }
            CreateOrUpdateUserEvent.OnAvatarClicked -> {
                sendEffect(CreateUpdateUserEffect.OpenImagePicker)
            }
            is CreateOrUpdateUserEvent.OnAvatarSelected -> {
                uploadAvatar(event.fileSource)
            }
            CreateOrUpdateUserEvent.OnSubmitClicked -> {
                if (currentState.userId == null) {
                    createUser()
                } else {
                    updateUser()
                }
            }
        }
    }

    private fun loadUserData(userId: String) {
        launchNetwork(
            onLoading = { isLoading -> updateState { it.copy(isLoading = isLoading) } },
            request = { getUserByIdUseCase.execute(GetUserByIdCommand(userId)) },
            onSuccess = { user ->
                updateState {
                    it.copy(
                        userId = user.id,
                        firstName = user.firstName,
                        lastName = user.lastName,
                        email = user.email ?: "",
                        phone = user.phone,
                        status = user.status,
                        avatarUrl = user.avatarUrl,
                        avatarMediaId = user.avatarMediaId
                    )
                }
            },
            onError = { _, message -> handleError(message) }
        )
    }

    private fun uploadAvatar(fileSource: FileSource) {
        launchNetwork(
            onLoading = { isLoading -> updateState { it.copy(isAvatarUploading = isLoading) } },
            request = { uploadImageUseCase.execute(UploadImageCommand(fileSource, ImageType.AVATAR)) },
            onSuccess = { result ->
                updateState {
                    it.copy(
                        avatarUrl = result.publicUrl,
                        avatarMediaId = result.mediaId
                    )
                }
            },
            onError = { _, message -> handleError(message, "Помилка завантаження") }
        )
    }

    private fun createUser() {
        val state = currentState
        if (state.firstName.isBlank() || state.lastName.isBlank()) {
            handleError(message = "Заповніть обов'язкові поля")
            return
        }

        launchNetwork(
            onLoading = { isLoading -> updateState { it.copy(isLoading = isLoading) } },
            request = { createUserUseCase.execute(CreateUserCommand(state.toDomainModel())) },
            onSuccess = { sendEffect(CreateUpdateUserEffect.CloseScreen) },
            onError = { _, message -> handleError(message, "Не вдалося створити користувача") }
        )
    }

    private fun updateUser() {
        val state = currentState
        if (state.firstName.isBlank() || state.lastName.isBlank()) {
            handleError(message = "Заповніть обов'язкові поля")
            return
        }

        launchNetwork(
            onLoading = { isLoading -> updateState { it.copy(isLoading = isLoading) } },
            request = { updateUserUseCase.execute(UpdateUserCommand(state.toDomainModel())) },
            onSuccess = { sendEffect(CreateUpdateUserEffect.CloseScreen) },
            onError = { _, message -> handleError(message, "Не вдалося оновити користувача") }
        )
    }

    private fun handleError(message: String?, default: String = "Сталася помилка") {
        val errorText = message?.let { UiText.DynamicString(it) }
            ?: UiText.StringResource(Res.string.error_alert_title)
        alertController.show(
            title = UiText.StringResource(Res.string.error_alert_title),
            message = errorText,
            type = NotificationType.ERROR
        )
    }
}